'use client';

import type { DetailHomeGiftProductResponse } from '__generated__/api/unauth';
import { useToast } from '@common/hooks';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { UnauthInfiniteQueryOptions } from '@/api';
import { type GiftCartInstitution, useGiftCart } from '@/app/donation/_hooks/useGiftCart';
import { GIFT_PRODUCT_SORT_TYPE } from '@/app/donation/constants';
import { DONATION_ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { filledInfiniteQueryOptions } from '@/utils/filledInfiniteQueryOptions';
import { RECOMMENDED_GIFTS_PAGE_SIZE } from '../../_constants';
import { fetchProductDetail } from '../../_utils';
import { RecommendedGiftsSection } from './RecommendedGiftsSection';
interface RecommendedGiftsContainerProps {
  institution: GiftCartInstitution;
  onOpenOptionSheet: (product: DetailHomeGiftProductResponse) => void;
}
export const RecommendedGiftsContainer = ({
  institution,
  onOpenOptionSheet
}: RecommendedGiftsContainerProps) => {
  const {
    push
  } = useCustomRouter();
  const queryClient = useQueryClient();
  const {
    toast
  } = useToast();
  const {
    addItem
  } = useGiftCart();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    ...filledInfiniteQueryOptions(UnauthInfiniteQueryOptions['get:/wello/v2/allows/home/gift-product']({
      query: {
        institutionId: institution.id,
        page: 1,
        size: RECOMMENDED_GIFTS_PAGE_SIZE,
        giftProductSortType: GIFT_PRODUCT_SORT_TYPE.POPULAR
      }
    })),
    enabled: !!institution.id
  });
  const recommendedGifts = useMemo(() => data?.pages.flatMap(page => page.context?.contents ?? []) ?? [], [data]);
  const handleAddToCart = async (productNumber: string) => {
    const gift_0 = recommendedGifts.find(gift => gift.productNumber === productNumber);
    if (!gift_0) return;
    const productDetail = await fetchProductDetail(queryClient, productNumber);
    if (!productDetail) {
      toast({
        message: '상품 정보를 불러오는데 실패했어요',
        type: 'fail'
      });
      return;
    }
    const hasOptions = productDetail.optionGroupInfoList && productDetail.optionGroupInfoList.length > 0;
    if (hasOptions) {
      onOpenOptionSheet(productDetail);
    } else {
      try {
        await addItem({
          giftProductNumber: productDetail.productNumber ?? '',
          quantity: 1
        });
        toast({
          message: '장바구니에 담았어요'
        });
      } catch {
        toast({
          message: '장바구니 담기에 실패했어요',
          type: 'fail'
        });
      }
    }
  };
  const handleMoreClick = () => {
    push(DONATION_ROUTES.GIFTS.withSearchParams({
      searchParams: {
        institutionId: institution.id
      }
    }));
  };
  const recommendedGiftCards = useMemo(() => recommendedGifts.map(gift_1 => ({
    productNumber: gift_1.productSlugId ?? gift_1.productNumber ?? '',
    imageUrl: gift_1.mainImageUrl,
    title: gift_1.productName ?? '',
    donationAmount: gift_1.requiredDonationAmount ?? 0,
    reviewRating: gift_1.reviewRating ?? 0,
    reviewCount: gift_1.reviewCount ?? 0,
    institution: {
      id: gift_1.institutionSlugId ?? gift_1.institutionId ?? 0,
      logoUrl: gift_1.institutionLogoUrl ?? '',
      name: gift_1.institutionName ?? ''
    },
    isAdult: gift_1.adultYN ?? false
  })), [recommendedGifts]);
  if (recommendedGiftCards.length === 0) {
    return null;
  }
  return <RecommendedGiftsSection gifts={recommendedGiftCards} onMoreClick={handleMoreClick} onLoadMore={() => fetchNextPage()} hasMore={hasNextPage} isLoadingMore={isFetchingNextPage} onAddToCart={handleAddToCart} />;
};